// ---------------------------------------------------------------------------
// DownloadMenu — popup de seleção de versão de download.
// ---------------------------------------------------------------------------
// Reusa o padrão oficial dos dropdowns da aplicação (`ActionDropdown` das
// views de resultados): `Popper + Grow + Paper + ClickAwayListener +
// MenuList + MenuItem` com os estilos de
// `components/actionDropdownStyle`. O objetivo é manter a sombra/borda
// consistentes com o restante do app.
//
// Cada item tem duas linhas: `label` (título) e `hint` (subtítulo). No
// hover o fundo vira `clientColor` e os dois textos ficam brancos.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import Popper from "@material-ui/core/Popper";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";

import actionDropdownStyle from "../../../../assets/jss/material-dashboard-pro-react/components/actionDropdownStyle";
import {
  grayColor,
  whiteColor,
} from "../../../../assets/jss/material-dashboard-pro-react";

// Estende o dropdownStyle com variações para itens em 2 linhas
// (label + hint). O hover do `clientColor` é herdado via `clientHover`.
const useStyles = makeStyles((theme) => {
  const base = actionDropdownStyle(theme);
  return {
    ...base,
    // Sobrescreve `dropdownItem` para acomodar duas linhas confortáveis.
    dropdownItem: {
      ...base.dropdownItem,
      padding: "8px 16px",
      whiteSpace: "normal",
      minWidth: 260,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 2,
      "&:hover $optionLabel, &:hover $optionHint": {
        color: whiteColor,
      },
    },
    optionLabel: {
      fontSize: 13,
      fontWeight: 500,
      color: grayColor[2],
    },
    optionHint: {
      fontSize: 11,
      color: grayColor[1],
    },
    // Item desabilitado (ex.: download do original em input sensivel):
    // opacidade reduzida, cursor bloqueado e sem hover do clientColor.
    disabledItem: {
      opacity: 0.45,
      cursor: "not-allowed",
      pointerEvents: "none",
    },
  };
});

const DownloadMenu = ({ anchorEl, open, onClose, options, onPick }) => {
  const classes = useStyles();
  const handleClickAway = (event) => {
    if (anchorEl && anchorEl.contains(event.target)) return;
    onClose();
  };
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      transition
      placement="bottom-start"
      className={open ? classes.popperResponsive : classes.popperClose}
      style={{ zIndex: 1400 }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} id="download-menu-list">
          <Paper className={classes.dropdown}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <MenuList role="menu" className={classes.menuList}>
                {options.map((opt) => (
                  <MenuItem
                    key={opt.kind}
                    disabled={!!opt.disabled}
                    onClick={opt.disabled ? undefined : () => onPick(opt.kind)}
                    className={`${classes.dropdownItem} ${
                      opt.disabled ? classes.disabledItem : classes.clientHover
                    }`}
                  >
                    <span className={classes.optionLabel}>{opt.label}</span>
                    {opt.hint && (
                      <span className={classes.optionHint}>{opt.hint}</span>
                    )}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

DownloadMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      hint: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onPick: PropTypes.func.isRequired,
};

export default DownloadMenu;
